import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import moment from 'moment';

/** TRANSLATION */
import { useTranslation } from 'react-i18next';

/** COMPONENT */
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import ButtonCustom from 'component/Button/ButtonCustom';

/** CONSTANTS */
import { Icons, icons, imgs } from 'constants/images';
import { colors } from 'constants/theme';

/** API */
import { fileUrl } from 'api/api';

/** UTILS */
import { getMember } from 'utils/app.utils';

/** STYLE */
import { styled } from '@mui/material/styles';
import { useCallback, useEffect, useState } from 'react';

import DrageMove from 'assets/icon/appointment/card/drag-move';
import Phone from 'assets/icon/follow-up/phone';
import ProfileCheck from 'assets/icon/follow-up/profile-check';
import { StatusSelectorCustom } from 'features/follow-up/follow-up-status-selector';
import _ from 'lodash';
import { Draggable } from 'react-beautiful-dnd';
import { dateToView } from 'utils/date.utils';
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils';


export const Card = styled('div')(({ theme }) => ({
  margin: '1rem 1rem 0',
  borderRadius: 4,
  boxShadow: `0 3px 6px 0 ${colors.black16}`,
  backgroundColor: colors.white,
  color: colors.textPrimary,
  cursor: 'pointer',
  '& p': {
    margin: 0
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: '0.75rem',
    marginRight: '0.75rem'
  }
}))

export const CardBody = styled('div')<{ isPastAppointment?: boolean }>(({ theme, isPastAppointment }) => ({
  position: 'relative',
  padding: '1.5rem 0.6rem 0.5rem 0.6rem',
  border: isPastAppointment ? '1px solid #EB8354' : `1px solid ${colors.extraLightGray}`,
  borderRadius: 4,
  minHeight: 123,
  backgroundColor: isPastAppointment ? '#FEE6DE' : colors.white,
  '& .number': {
    position: 'absolute',
    top: 0,
    left: 0,
    minWidth: 27,
    minHeight: 28,
    color: colors.white,
    background: `${colors.themeMainColor} !important`,
    borderRadius: '4px 0 20px',
    padding: '2px 0 0 4px',
    fontSize: 14,
    lineHeight: 'normal'
  },
  '& .queue': {
    position: 'absolute',
    top: 8,
    right: 8,
    color: colors.statusInactive,
    padding: '1px 6.7px',
    fontSize: 12,
    lineHeight: 'normal'
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem'
  }
}))

export const CardFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  '> div': {
    width: '100%',
    '.MuiButton-outlined': {
      width: '100%',
      borderRadius: 0,
      borderColor: `${colors.extraLightGray} !important`,
      fontSize: 16,
      textTransform: 'capitalize',
      height: 49,
      img: {
        width: 16,
        height: 16
      },
      '.MuiButton-startIcon': {
        marginLeft: 0
      }
    },
    '&:first-of-type .MuiButton-outlined': {
      borderBottomLeftRadius: 4
    },
    '&:last-of-type .MuiButton-outlined': {
      borderBottomRightRadius: 4
    },
    '.btn-payment': {
      borderColor: 'transparent !important',
      paddingLeft: 0
    }
  },
  '.btn-icon-group': {
    display: 'flex',
    justifyContent: 'flex-end',
    '> div': {
      margin: '0 !important',
      '&:first-of-type .MuiButton-outlined': {
        borderRadius: '0 !important'
      }
    },
    '.btn-icon': {
      borderColor: 'transparent !important',
      minWidth: 48,
      padding: 0,
      '.MuiButton-startIcon': {
        margin: 0
      }
    }
  },
  [theme.breakpoints.down('sm')]: {
    button: {
      paddingLeft: 0,
      paddingRight: 0
    }
  }
}))


type CardServicePointType = {
  patient: any
  number: number
  serviceType: string
  branch: string
  onClick?: (val: any) => void
  onCancel?: (val: any) => void
  onSendMessage?: (val: any) => void
  draggableId?: string
  isDrag?: boolean
  onMouseDown?: () => void
  dragProvided?: any
  autoIncrement?: string
}

export default function CardFollowUp(props: CardServicePointType) {
  const { t } = useTranslation()
  const [branch, setBranch] = useState<any>()
  const memberAll = getMember()


  useEffect(() => {
    setBranch(props.branch)
  }, [props.branch])


  const handlePatientCode = useCallback(() => {
    switch (branch?.patientCodeQueue) {
      case '1': return props.patient.cnNumber ?? '';
      case '0': return props.patient.branchCnNumber ?? '';
      case '2': return props.patient.snCode ?? '';
      default: return props.patient.cnNumber
      // default: return '';
    }
  }, [branch?.patientCodeQueue, props.patient])

  const renderImage = (img: string) => img ? `${fileUrl}/${img}` : imgs.defaultAvatar


  const renderDateNow = useCallback(() => {
    const now = moment()
    const duration = moment.duration(now.diff(props.patient.stageUpdatedAt))
    const minutesAgo = duration.asMinutes()

    if (minutesAgo <= 1) return 'ไม่กี่วินาทีแล้ว'
    if (minutesAgo <= 60) return `${Math.floor(minutesAgo)} ${t('MINUTE_AGO')}`
    return moment(props.patient.stageUpdatedAt).fromNow()
  }, [props.patient.stageUpdatedAt, t])

  const isDateAppointmentPast = useCallback(() => {
    const dateStr = props.patient?.dateAppointment
    // ถ้าไม่มีวันนัด (null/undefined/empty) ให้ถือว่าเป็น past ทันที
    if (!dateStr) return true

    // รองรับรูปแบบวันที่ทั้งแบบสากล (YYYY-MM-DD) และแบบไทย (DD-MM-YYYY) + ปีพุทธศักราช
    let year: number | undefined
    let month: number | undefined
    let day: number | undefined

    const parts = dateStr.split('-')
    if (parts.length === 3) {
      // ถ้าส่วนแรกยาว 4 ตัวอย่างเช่น 2026 / 2569 ให้ถือว่าเป็น YYYY-MM-DD หรือ YYYY-MM-DD (พ.ศ.)
      if (parts[0].length === 4) {
        year = Number(parts[0])
        month = Number(parts[1])
        day = Number(parts[2])
      } else {
        // กรณีเช่น 26-02-2569 → DD-MM-YYYY (พ.ศ. หรือ ค.ศ.)
        day = Number(parts[0])
        month = Number(parts[1])
        year = Number(parts[2])
      }
    }

    if (!year || !month || !day) return false

    // ถ้าเป็นปี พ.ศ. ให้แปลงเป็น ค.ศ. โดยลบ 543
    if (year > 2400) year = year - 543

    const appointmentDate = moment({ year, month: month - 1, date: day })
    if (!appointmentDate.isValid()) return false

    const appointmentDateStr = appointmentDate.format('YYYY-MM-DD')
    const todayStr = moment().format('YYYY-MM-DD')

    // ถ้าวันนัดเป็นวันนี้ ให้ถือว่าไม่เลยกำหนด (false)
    if (appointmentDateStr === todayStr) return false

    // เป็น true เฉพาะวันก่อนหน้าวันนี้เท่านั้น
    return appointmentDateStr < todayStr
  }, [props.patient?.dateAppointment])




  return (
    <CardDraggable
      number={props.number}
      serviceType={props.serviceType}
      draggableId={props?.draggableId}
      isDrag={props?.isDrag || false}
      onCancel={props?.onCancel}
      onSendMessage={props?.onSendMessage}
    >
      <CardBody
        className={'d-flex flex-column'}
        onClick={props.onClick}
        isPastAppointment={isDateAppointmentPast()}
      >
        <Typography className={'number'}>{String(props.number + 1).padStart(2, '0')}</Typography>
        <Typography className={'queue'} style={{ fontSize: 12, color: colors.statusInactive }}> {renderDateNow()} </Typography>
        <Box className='d-flex'>
          <Box sx={{ maxWidth: '50px' }}>
            <Avatar alt={props.patient.cnNumber} src={renderImage(props.patient.avatar)} className="mx-auto" sx={{ width: 50, height: 50, border: `3px solid ${_.find(memberAll, { rankId: props.patient.rankId })}`, backgroundColor: colors.white }}>
              <img alt='' src={imgs.defaultAvatar} className="w-100 h-100" />
            </Avatar>
          </Box>
          <Box className='pl-3 overflow-hidden'>
            <Tooltip title={`${handlePatientCode()} ${props.patient?.firstname} ${props.patient?.lastname}`}><Typography color='#29302E' className='text-ellipsis text-medium' style={{ fontWeight: 500, fontSize: '16px' }}>
              {handlePatientCode()} {props.patient?.firstname} {props.patient?.lastname}
            </Typography></Tooltip>
            <Box sx={{ display: 'flex', justifyItems: 'center' }}>
              <Box marginTop={'-3px'}><Phone fill={'#29302E'} height='16' /> </Box>
              <Box marginX='4px' />
              <Typography fontSize="14px" color={'#29302E'} fontWeight={400} marginLeft='6px'> {props.patient?.phone ?? '-'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyItems: 'center' }}>
              {(props.patient?.courseStatus === 'DELETED' || props.patient?.courseStatus === 'CANCEL') && (<Tooltip title={
                <>
                  {props.patient?.courseStatus === 'DELETED' && <div>{t('ลบคอร์ส')}</div>}
                  {props.patient?.courseStatus === 'CANCEL' && <div>{t('ยกเลิกคอร์ส')}</div>}
                </>
              }><Box marginTop={'-3px'}><ErrorIcon sx={{ width: '16px', height: '16px', color: '#FB5252' }} /> </Box></Tooltip>)}
              <Box marginX='4px' />
              {props.patient?.treatmentCourseName !== "" && <Tooltip title={<>{props.patient?.treatmentCourseName}</>}><Typography color='#29302E' className='text-ellipsis text-medium' style={{ fontWeight: 500, fontSize: '14px' }}>
                {`${t('คอร์ส')} :`} {props.patient?.treatmentCourseName === "" ? '-' : props.patient?.treatmentCourseName}
              </Typography></Tooltip>}
              {props.patient?.treatmentCourseName === "" && <Typography color='#29302E' className='text-ellipsis text-medium' style={{ fontWeight: 500, fontSize: '14px' }}>
                {`${t('คอร์ส')} :`} {props.patient?.treatmentCourseName === "" ? '-' : props.patient?.treatmentCourseName}
              </Typography>}
            </Box>
          </Box>
        </Box>
        <Typography color='#29302E' className='text-ellipsis text-medium' style={{ fontWeight: 500, fontSize: '14px' }}>
          {`${t('วันที่นัดถัดไป')} :`} {props.patient?.dateAppointment ? props.patient?.dateAppointment?.replace(/-/g, '/') : '-'}
        </Typography>
        <Box className='d-flex' sx={{ paddingY: '5px' }}>
          <Tooltip title={
            props.patient?.operatives && props.patient.operatives.length > 0
              ? _.map(props.patient.operatives, (operative) => operative?.operativeName ?? operative?.operativeTypeName ?? '-').join(', ')
              : ""
          }>
            <Typography className={'m-0 text-ellipsis'}>
              <Box>
                <Box className='d-flex align-items-start' sx={{ marginTop: '-2px' }}>
                  <Box className='d-flex align-items-center' sx={{ flex: 'none' }}>
                    <Box className='d-flex align-items-center justify-content-center' sx={{ flex: 'none', width: '25px', svg: { width: '20px' } }}>
                      <img src={icons.follow_up.followUpIconTooth} style={{ marginRight: '2px' }} /> <span>{':'}</span>
                    </Box>
                  </Box>
                  {_.map(props.patient?.operatives, (operative, idx) => {
                    return <Box key={idx} sx={{
                      marginTop: '4px',
                      marginLeft: '2px',
                      bgcolor: `${operative?.color}`,
                      borderRadius: `16px`,
                      padding: '1px 1px',
                      color: '#29302E',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '12px'
                    }}><span className='mx-1'>{operative?.operativeName || operative?.operativeTypeName || '-'}</span></Box>
                  })}
                </Box>
              </Box>
            </Typography>
          </Tooltip>
        </Box>
        <Box className='d-flex' sx={{ paddingBottom: '5px' }}>
          <Box marginRight={'6px'}><Typography color='#29302E' fontSize="14px"> {`${t('สถานะ')} :`} </Typography></Box>
          <StatusSelectorCustom status={props.patient?.followUpStatus} unshow />
        </Box>
        <Box display='flex' justifyContent='space-between'>
          <Typography color='#29302E' fontSize="11px">
            {`${t('ติดตามล่าสุด')} :`} {props.patient?.lastFollowUp ? dateToView(props.patient?.lastFollowUp) : '-'}
          </Typography>
          <Typography color='#29302E' fontSize="11px">
            {`${t('ติดตามถัดไป')} :`} {props.patient?.dateFollowUp ? props.patient?.dateFollowUp.replace(/-/g, '/') : '-'}
          </Typography>
        </Box>
      </CardBody>
    </CardDraggable >
  )
}

interface CardFooterDraggableProps {
  serviceType: string
  dragProvided?: any
  dragSnapshot?: any

  hideFooterAction?: boolean
  onCancel?: (val: any) => void
  onPayment?: (val: any) => void
  onSendMessage?: (val: any) => void

}

const CardFooterDraggable = (props: CardFooterDraggableProps) => {
  const { t } = useTranslation()
  const permissions = { isCreate: isCreate(), isDelete: isDelete(), isUpdate: isUpdate(), isView: isView() }
  const styleBTN = {
    '.MuiButton-outlined': {
      padding: '1'
    }
  }


  return (
    <>
      {!props.hideFooterAction && (
        <CardFooter>
          {(props?.dragProvided && props.serviceType !== 'FOLLOW_UP') &&
            <>
              <div
                style={{
                  width: 'fit-content',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0 8px'
                }}
                {...(!permissions.isUpdate.disabled && props?.dragProvided?.dragHandleProps)}
              >
                <DrageMove
                  fill={
                    permissions.isUpdate.disabled
                      ? colors.disabledGray
                      : props?.dragSnapshot?.isDragging
                        ? colors.themeSecondColor
                        : undefined
                  }
                  width={'20px'}
                  height={'20px'}
                />
              </div>
            </>
          }

          <>
            <ButtonCustom
              style={{
                ...styleBTN,
                '&.Mui-disabled': {
                  opacity: 0.7,
                  '&.MuiButton-contained': {
                    background: `${colors.disabledGray} !important`,
                    backgroundColor: `${colors.disabledGray} !important`,
                  },
                  '&.MuiButton-outlined': {
                    border: `1px solid #F5F5F5 !important`,
                    color: `${colors.disabledGray} !important`
                  }
                }
              }}
              startIcon={<ProfileCheck fill={permissions.isUpdate.disabled ? colors.disabledGray : colors.themeSecondColor} stroke={permissions.isUpdate.disabled ? colors.disabledGray : colors.themeSecondColor} />}
              textButton={t('ติดตาม')}
              type="button"
              variant="outlined"
              onClick={props.onSendMessage}
              disabled={permissions.isUpdate.disabled}
            />
            <ButtonCustom
              style={{
                ...styleBTN,
                '&.Mui-disabled': {
                  opacity: 0.7,
                  '&.MuiButton-contained': {
                    background: `${colors.disabledGray} !important`,
                    backgroundColor: `${colors.disabledGray} !important`,
                  },
                  '&.MuiButton-outlined': {
                    border: `1px solid #F5F5F5 !important`,
                    color: `${colors.disabledGray} !important`
                  }
                },
              }}
              startIcon={<Icons.IconClose color={permissions.isUpdate.disabled ? colors.disabledGray : colors.statusDangerColor} width='12px' height='12px' />}
              textButton={t('เลิกติดตาม')}
              type="button"
              variant="outlined"
              onClick={!permissions.isUpdate.disabled ? props.onCancel : undefined}
              disabled={permissions.isUpdate.disabled}
              mode="del"
            />
            {/* <Box
              display="flex"
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
              gap="8px"
              onClick={!permissions.isUpdate.disabled ? props.onCancel : undefined}
              sx={{
                color: permissions.isUpdate.disabled ? colors.disabledGray : '#FB5252',
                cursor: permissions.isUpdate.disabled ? 'not-allowed' : 'pointer',
              }}>
              <Icons.IconClose color={permissions.isUpdate.disabled ? colors.disabledGray : "#FB5252"} width='14px' height='14px' />
              <Typography fontSize="16px" color={'#9E9E9E'}>{t('เลิกติดตาม')}</Typography>
            </Box> */}
          </>
        </CardFooter >
      )}
    </>
  )
}

interface CardDraggableProps {
  number: number
  serviceType: string
  children: any
  draggableId?: string
  isDrag?: boolean
  onCancel?: (val: any) => void
  onPayment?: (val: any) => void
  onSendMessage?: (val: any) => void
}

const CardDraggable = (props: CardDraggableProps) => {

  return (
    <Draggable
      key={props?.number}
      draggableId={`${props?.draggableId}`}
      index={props?.number}
      isDragDisabled={props?.isDrag || false}
    >
      {(dragProvided, dragSnapshot) => {
        return (
          <Card
            {...dragProvided.draggableProps}
            ref={dragProvided.innerRef}
          >
            {props.children}
            <CardFooterDraggable
              dragProvided={dragProvided}
              dragSnapshot={dragSnapshot}
              serviceType={props.serviceType}
              onCancel={props?.onCancel}
              onSendMessage={props?.onSendMessage}
            />
          </Card>
        )
      }}
    </Draggable>
  )
}

